/**
 * Target Renderer — draws targets for Target Blast mode.
 *
 * Targets are glowing circles that pulse when active and flash green when hit.
 * Exit gate is a portal at P2's former spawn position.
 * Lives in the world container (affected by camera).
 */

import { Container, Graphics } from 'pixi.js';
import type { Target } from '../types';
import { TARGET_BLAST, Z } from '../constants';

interface TargetVisual {
  gfx: Graphics;
  hitFlashTimer: number;
}

export class TargetRenderer {
  private container: Container;
  private visuals: TargetVisual[] = [];
  private elapsed = 0;

  // Exit gate
  private gateGfx: Graphics;
  private gateX = 0;
  private gateY = 0;
  private gateActive = false;
  private gateVisible = false;

  constructor(worldContainer: Container) {
    this.container = new Container();
    this.container.zIndex = Z.TARGETS;
    worldContainer.addChild(this.container);

    // Exit gate graphics
    this.gateGfx = new Graphics();
    this.gateGfx.zIndex = Z.TARGETS - 1;
    this.container.addChild(this.gateGfx);
  }

  /** Rebuild target visuals from current target list. Call on level load/reset. */
  rebuild(targets: Target[]): void {
    // Clear old
    for (const v of this.visuals) {
      v.gfx.destroy();
    }
    this.visuals = [];

    // Create new
    for (const _t of targets) {
      const gfx = new Graphics();
      this.container.addChild(gfx);
      this.visuals.push({ gfx, hitFlashTimer: 0 });
    }
  }

  /** Set exit gate position (called from main.ts when mode state has gate coords). */
  setExitGate(x: number, y: number): void {
    this.gateX = x;
    this.gateY = y;
    this.gateVisible = true;
    this.gateActive = false;
  }

  /** Activate/deactivate the exit gate (green pulsing portal). */
  setExitGateActive(active: boolean): void {
    this.gateActive = active;
  }

  /** Update each frame (variable timestep). */
  update(targets: Target[], dt: number): void {
    this.elapsed += dt;

    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      const visual = this.visuals[i];
      if (!visual) continue;

      visual.gfx.clear();

      if (target.hit) {
        // Hit target — brief green flash then fade out
        if (visual.hitFlashTimer < TARGET_BLAST.TARGET_HIT_FLASH_DURATION) {
          visual.hitFlashTimer += dt;
          const t = visual.hitFlashTimer / TARGET_BLAST.TARGET_HIT_FLASH_DURATION;
          const alpha = 1 - t;

          // Green ring expanding outward
          const expandRadius = target.radius + t * target.radius * 2;
          visual.gfx.circle(target.x, target.y, expandRadius);
          visual.gfx.stroke({ color: TARGET_BLAST.TARGET_HIT_COLOR, width: 3, alpha: alpha * 0.8 });
        }
        // After flash, graphics stay empty (target disappears)
        continue;
      }

      // Active target — pulsing amber circle
      const pulse = 1 + Math.sin(this.elapsed * TARGET_BLAST.TARGET_PULSE_SPEED + i * 1.5) * 0.15;
      const r = target.radius * pulse;

      // Outer glow
      visual.gfx.circle(target.x, target.y, r * 1.6);
      visual.gfx.fill({ color: TARGET_BLAST.TARGET_COLOR, alpha: TARGET_BLAST.TARGET_GLOW_ALPHA * 0.3 });

      // Inner glow
      visual.gfx.circle(target.x, target.y, r * 1.2);
      visual.gfx.fill({ color: TARGET_BLAST.TARGET_COLOR, alpha: TARGET_BLAST.TARGET_GLOW_ALPHA });

      // Core ring
      visual.gfx.circle(target.x, target.y, r);
      visual.gfx.stroke({ color: TARGET_BLAST.TARGET_COLOR, width: 3 });

      // Crosshair lines
      const lineLen = r * 0.6;
      visual.gfx.moveTo(target.x - lineLen, target.y);
      visual.gfx.lineTo(target.x + lineLen, target.y);
      visual.gfx.stroke({ color: TARGET_BLAST.TARGET_COLOR, width: 1.5, alpha: 0.6 });
      visual.gfx.moveTo(target.x, target.y - lineLen);
      visual.gfx.lineTo(target.x, target.y + lineLen);
      visual.gfx.stroke({ color: TARGET_BLAST.TARGET_COLOR, width: 1.5, alpha: 0.6 });

      // Center dot
      visual.gfx.circle(target.x, target.y, 3);
      visual.gfx.fill({ color: 0xffffff, alpha: 0.9 });
    }

    // --- Exit gate rendering ---
    this.gateGfx.clear();
    if (!this.gateVisible) return;

    const gateR = TARGET_BLAST.GATE_RADIUS;
    const gx = this.gateX;
    const gy = this.gateY;

    if (this.gateActive) {
      // Active — pulsing green portal
      const pulse = 1 + Math.sin(this.elapsed * TARGET_BLAST.GATE_PULSE_SPEED) * 0.12;
      const pr = gateR * pulse;

      // Outer glow
      this.gateGfx.circle(gx, gy, pr * 1.8);
      this.gateGfx.fill({ color: TARGET_BLAST.GATE_GLOW_COLOR, alpha: 0.08 });

      // Mid glow
      this.gateGfx.circle(gx, gy, pr * 1.4);
      this.gateGfx.fill({ color: TARGET_BLAST.GATE_ACTIVE_COLOR, alpha: 0.12 });

      // Inner portal fill
      this.gateGfx.circle(gx, gy, pr);
      this.gateGfx.fill({ color: TARGET_BLAST.GATE_ACTIVE_COLOR, alpha: 0.15 + Math.sin(this.elapsed * 6) * 0.05 });

      // Ring
      this.gateGfx.circle(gx, gy, pr);
      this.gateGfx.stroke({ color: TARGET_BLAST.GATE_ACTIVE_COLOR, width: TARGET_BLAST.GATE_RING_WIDTH });

      // Inner ring (brighter)
      this.gateGfx.circle(gx, gy, pr * 0.6);
      this.gateGfx.stroke({ color: 0xffffff, width: 1.5, alpha: 0.4 });

      // Center dot
      this.gateGfx.circle(gx, gy, 4);
      this.gateGfx.fill({ color: 0xffffff, alpha: 0.8 });
    } else {
      // Inactive — dim amber ring
      this.gateGfx.circle(gx, gy, gateR);
      this.gateGfx.stroke({ color: TARGET_BLAST.GATE_INACTIVE_COLOR, width: 2, alpha: 0.3 });

      // Subtle fill
      this.gateGfx.circle(gx, gy, gateR);
      this.gateGfx.fill({ color: TARGET_BLAST.GATE_INACTIVE_COLOR, alpha: 0.04 });
    }
  }

  /** Show/hide the target container. */
  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  destroy(): void {
    for (const v of this.visuals) {
      v.gfx.destroy();
    }
    this.visuals = [];
    this.gateGfx.destroy();
    this.container.destroy({ children: true });
  }
}
